import { Button, useNotify } from "react-admin";
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  makeStyles,
  TextField,
  Typography,
} from "@material-ui/core";
import React, { useState } from "react";
import { useField } from "react-final-form";
import { find } from "lodash";
import Autocomplete from "@material-ui/lab/Autocomplete";
import SanitizedGrid from "../../../components/SanitizedGrid";
import {
  removeSchoolLocationFromRouteLocation,
  reOptimiseRouteLocation,
} from "../tools";
import AiLoading from "./AiLoading";

const useStyles = makeStyles({
  dialog: {
    "& .MuiDialog-paper": {
      overflowY: "unset",
      width: "100%",
    },
  },
  autocompleteInput: {
    marginTop: "8px",
    paddingBottom: "3px",
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "#EAEAEA",
      },
      "&:hover fieldset": {
        borderColor: "#EAEAEA",
      },
    },
  },
});

interface MergeStudentsButtonProps {
  selectedLocationOrders: number[];
  setSelectedLocationOrders: React.Dispatch<React.SetStateAction<number[]>>;
  school: ISchool;
  busSchedule: IBusSchedule;
  maxStopTime: number;
}

const MergeStudentsButton: React.FC<MergeStudentsButtonProps> = ({
  selectedLocationOrders,
  setSelectedLocationOrders,
  school,
  busSchedule,
  maxStopTime,
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedRouteLocation, setSelectedRouteLocation] = useState<any>(null);

  const routeLocationsField = useField("routeLocations");
  const routeGeometriesField = useField(`routeGeometries`);

  const classes = useStyles();
  const notify = useNotify();

  const handleOpenDialog = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setSelectedLocationOrders([]);
    setOpen(false);
  };

  const handleSaveMergeLocation = async () => {
    try {
      setIsLoading(true);

      const selectedOrder = selectedRouteLocation.order;

      const selectedLocations = routeLocationsField.input.value.filter(
        (location: any) => selectedLocationOrders.includes(location.order),
      );

      const mergedStudents = selectedLocations.flatMap(
        (location: any) => location.students,
      );

      const updatedSelectedLocation = {
        ...selectedRouteLocation,
        students: mergedStudents,
      };

      const remainingLocations = routeLocationsField.input.value.filter(
        (location: any) => !selectedLocationOrders.includes(location.order),
      );

      const insertIndex = remainingLocations.findIndex(
        (loc: any) => loc.order >= selectedOrder,
      );

      if (insertIndex > -1) {
        remainingLocations.splice(insertIndex, 0, updatedSelectedLocation);
      } else {
        // If not found, append at the end
        remainingLocations.push(updatedSelectedLocation);
      }

      const routeLocationWithoutSchool = removeSchoolLocationFromRouteLocation(
        remainingLocations,
        busSchedule.type,
      );

      const optimisedRouteLocations = await reOptimiseRouteLocation(
        school,
        busSchedule,
        maxStopTime,
        routeLocationWithoutSchool,
      );

      routeLocationsField.input.onChange(optimisedRouteLocations);
      routeGeometriesField.input.onChange([]);
      setIsLoading(false);
      handleClose();
    } catch (error) {
      notify("Failed to merge students", "error");
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button
        label="Merge Students"
        size="small"
        style={{ marginRight: "10px" }}
        onClick={handleOpenDialog}
      />

      <Dialog
        open={open}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className={classes.dialog}
        maxWidth="md"
      >
        <DialogTitle id="alert-dialog-title">Merge Students</DialogTitle>
        <DialogContent>
          <SanitizedGrid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle2">Students to be merged</Typography>
            </Grid>
            <Grid item xs={12}>
              <ul
                style={{
                  margin: 5,
                  maxHeight: "200px",
                  overflowY: "auto",
                }}
              >
                {selectedLocationOrders.map((order: number) => (
                  <div key={order}>
                    {routeLocationsField.input.value
                      .find((location: any) => location.order === order)
                      .students.map((student: any) => (
                        <li key={student.id}>
                          {student.firstName} {student.lastName}
                        </li>
                      ))}
                  </div>
                ))}
              </ul>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle2">Merge to</Typography>
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                className={classes.autocompleteInput}
                freeSolo
                disableClearable
                options={routeLocationsField.input.value
                  .filter((option: any) =>
                    selectedLocationOrders.includes(option.order),
                  )
                  .map((option: any) => option.address)}
                onChange={(_event, value: any) => {
                  setSelectedRouteLocation(
                    find(routeLocationsField.input.value, ["address", value]),
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select address to merge the students"
                    variant="outlined"
                    InputProps={{ ...params.InputProps, type: "search" }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              Are you sure to merge students in to this location?
            </Grid>
          </SanitizedGrid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary" label="Back" />
          <Button
            onClick={handleSaveMergeLocation}
            color="primary"
            label="Save"
            startIcon={
              isLoading && (
                <CircularProgress size={20} style={{ color: "#FFFFFF" }} />
              )
            }
          />
        </DialogActions>
      </Dialog>

      <Dialog
        open={isLoading}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="md"
      >
        <AiLoading isLoading={isLoading} />
      </Dialog>
    </>
  );
};

export default MergeStudentsButton;
